// ============================================
// PLUPack · "Pagar"
// ============================================
// When the customer taps "Pagar" (cart drawer or cart page), the order is
// loaded into a PLUPack truck, its rear doors close and it drives off while
// the secure checkout opens. Shopify's checkout and thank-you pages can't run
// theme code, so this moment happens here, just before leaving the store.
// The truck is drawn in 3D by assets/plu-fx.js, which this file loads on
// every page once it is idle; until it is ready, or without WebGL, the CSS
// truck below plays instead. Styles live in assets/plu-shop.css.

/** When the doors are shut and the checkout starts loading, in ms. */
const LEAVE_AT = 1250;
/** If the checkout still hasn't opened by then, the overlay goes away, in ms. */
const GIVE_UP_AT = 10000;

// The truck's cargo box, in px: width, height, length, and the frame of the rear opening
const W = 150;
const H = 118;
const L = 220;
const T = 8;
const DOOR_W = (W - 2 * T) / 2 + 1;
const DOOR_H = 106;

/**
 * A flat piece of the truck, centered on the point its transform moves it to.
 * @param {string} className
 * @param {number} width
 * @param {number} height
 * @param {string} transform
 * @param {string} [content]
 */
function plane(className, width, height, transform, content = '') {
  return `<div class="${className}" style="width:${width}px;height:${height}px;margin:${-height / 2}px 0 0 ${-width / 2}px;transform:${transform}">${content}</div>`;
}

/**
 * A carton, with the three faces the camera sees (back, right side and top).
 * @param {{x: number, y: number, z: number, w: number, h: number, d: number}} box
 * @param {string} [className]
 */
function carton({ x, y, z, w, h, d }, className = '') {
  return `<div class="plu-truck__carton ${className}" style="transform:translate3d(${x}px,${y}px,${z}px)"><div class="plu-truck__carton-pieces">${plane(
    'plu-truck__carton-back',
    w,
    h,
    `translateZ(${d / 2}px)`
  )}${plane('plu-truck__carton-side', d, h, `translateX(${w / 2}px) rotateY(90deg)`)}${plane(
    'plu-truck__carton-top',
    w,
    d,
    `translateY(${-h / 2}px) rotateX(90deg)`
  )}</div></div>`;
}

/** @param {'left' | 'right'} side */
function door(side) {
  const x = side === 'left' ? -W / 2 + T - 1 : W / 2 - T + 1;
  // the left door closes last, a hair in front of the right one
  const z = L / 2 + (side === 'left' ? 1.2 : 0.6);
  const left = side === 'left' ? 0 : -DOOR_W;
  return `<div class="plu-truck__hinge" style="transform:translate3d(${x}px,0,${z}px)"><div class="plu-truck__door plu-truck__door--${side}" style="width:${DOOR_W}px;height:${DOOR_H}px;top:-52px;left:${left}px"><div class="plu-truck__door-out"></div><div class="plu-truck__door-in"></div></div></div>`;
}

/** @param {number} z */
function wheel(z) {
  return plane(
    'plu-truck__wheel',
    46,
    46,
    `translate3d(${W / 2 + 2}px,${H / 2 + 12}px,${z}px) rotateY(90deg)`,
    '<span class="plu-truck__rim"></span>'
  );
}

function truck() {
  const floor = H / 2 - 6;
  const cargo = [
    { x: -38, y: floor - 22, z: -86, w: 56, h: 44, d: 44 },
    { x: -36, y: floor - 63, z: -88, w: 50, h: 38, d: 40 },
    { x: 22, y: floor - 25, z: -84, w: 60, h: 50, d: 48 },
    { x: -44, y: floor - 18, z: -30, w: 44, h: 36, d: 44 },
  ];
  const order = { x: 8, y: floor - 19, z: 50, w: 46, h: 38, d: 40 };
  const cab = { y: 25, z: -L / 2 - 38, w: 140, h: 84, d: 72 };

  return [
    // inside, seen through the rear opening
    plane('plu-truck__floor', W, L, `translateY(${floor}px) rotateX(90deg)`),
    plane('plu-truck__wall', L, H, `translateX(${-W / 2 + T}px) rotateY(90deg)`),
    plane('plu-truck__wall-front', W, H, `translateZ(${-L / 2 + 2}px)`),
    ...cargo.map((box) => carton(box, 'plu-truck__carton--inside')),
    carton(order, 'plu-truck__carton--order'),
    // the cab, ahead of the cargo box
    plane('plu-truck__cab-side', cab.d, cab.h, `translate3d(${cab.w / 2}px,${cab.y}px,${cab.z}px) rotateY(90deg)`),
    plane('plu-truck__cab-roof', cab.w, cab.d, `translate3d(0,${cab.y - cab.h / 2}px,${cab.z}px) rotateX(90deg)`),
    // the cargo box
    plane('plu-truck__chassis', L - 20, 14, `translate3d(${W / 2 - 4}px,${H / 2 + 7}px,0) rotateY(90deg)`),
    plane(
      'plu-truck__side',
      L,
      H,
      `translateX(${W / 2}px) rotateY(90deg)`,
      '<span class="plu-truck__logo">PLUPack</span><span class="plu-truck__tagline">Embalajes · Descartables · Limpieza</span>'
    ),
    plane('plu-truck__roof', W, L, `translateY(${-H / 2}px) rotateX(90deg)`),
    plane('plu-truck__rear', W, H, `translateZ(${L / 2}px)`),
    plane('plu-truck__bumper', W + 6, 10, `translate3d(0,${H / 2 + 6}px,${L / 2 + 2}px)`),
    wheel(64),
    wheel(cab.z),
    door('right'),
    door('left'),
  ].join('');
}

/** The CSS truck and its ground. */
function cssStage() {
  return `
      <div class="plu-truck__stage" aria-hidden="true">
        <span class="plu-truck__ground"></span>
        <div class="plu-truck__camera">
          <div class="plu-truck__body"><div class="plu-truck__rig">${truck()}</div></div>
        </div>
      </div>`;
}

/** @param {boolean} gl - leave the stage empty for the 3D truck */
function overlay(gl) {
  const dialog = document.createElement('dialog');
  dialog.className = 'plu-truck';
  dialog.tabIndex = -1;
  dialog.setAttribute('aria-labelledby', 'plu-truck-title');
  dialog.innerHTML = `
    <div class="plu-truck__scene">${gl ? '<div class="plu-truck__stage plu-truck__stage--gl" aria-hidden="true"></div>' : cssStage()}
      <p class="plu-truck__title" id="plu-truck-title">¡Gracias por elegir PLUPack!</p>
      <p class="plu-truck__text">Cargamos tu pedido y te llevamos al pago seguro<span class="plu-truck__dots" aria-hidden="true"></span></p>
    </div>`;
  return dialog;
}

// ============================================
// 3D (assets/plu-fx.js)
// ============================================

/**
 * The 3D truck, once loaded and prewarmed.
 * @type {{ isReady(): boolean, playTruck(stage: HTMLElement): { stop(): void } | null } | null}
 */
let pluFx = null;
/** @type {{ stop(): void } | null} */
let truck3d = null;

/** Loads the 3D effects when the page is idle (every page has a cart). */
function requestFx() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const device = /** @type {any} */ (navigator);
  // data saver or a very small device: the CSS version is lighter
  if (device.connection?.saveData || (device.deviceMemory && device.deviceMemory < 2) || (navigator.hardwareConcurrency || 4) < 3) return;
  const load = () => {
    import('@theme/plu-fx')
      .then((module) => {
        pluFx = module;
        return module.prewarm();
      })
      .catch(() => {
        // no 3D: the CSS truck stays
      });
  };
  // well after the page has loaded, in an idle moment, while the page is on screen
  const idle = () => {
    if (document.visibilityState !== 'visible') {
      document.addEventListener('visibilitychange', idle, { once: true });
      return;
    }
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(load, { timeout: 5000 });
    else window.setTimeout(load, 1500);
  };
  // only once the visitor starts using the page (scroll, touch, mouse or key),
  // so a quick visit never downloads it; well after the page has loaded
  const events = ['scroll', 'pointerdown', 'pointermove', 'touchstart', 'keydown'];
  const start = () => {
    events.forEach((name) => window.removeEventListener(name, start));
    if (document.readyState === 'complete') window.setTimeout(idle, 1000);
    else window.addEventListener('load', () => window.setTimeout(idle, 1000), { once: true });
  };
  events.forEach((name) => window.addEventListener(name, start, { once: true, passive: true }));
}

requestFx();

/** @type {HTMLDialogElement | null} */
let active = null;
let leaveTimer = 0;
let giveUpTimer = 0;
/** The resubmission that actually opens the checkout goes through untouched. */
let leaving = false;

function reset() {
  window.clearTimeout(leaveTimer);
  window.clearTimeout(giveUpTimer);
  leaving = false;
  truck3d?.stop();
  truck3d = null;
  if (active) {
    if (active.open) active.close();
    active.remove();
  }
  active = null;
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  const button = event.submitter;
  if (!(form instanceof HTMLFormElement) || !(button instanceof HTMLButtonElement)) return;
  if (button.name !== 'checkout' || event.defaultPrevented) return;
  if (leaving) return;
  if (active) {
    // already on its way
    event.preventDefault();
    return;
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  event.preventDefault();

  const leave = () => {
    if (leaving) return;
    window.clearTimeout(leaveTimer);
    // the cart may have been drawn again meanwhile: use the button that is there now
    const submitter = button.isConnected ? button : document.querySelector('button[name="checkout"]:not([disabled])');
    const owner = submitter instanceof HTMLButtonElement ? submitter.form : null;
    const checkoutUrl = `${/** @type {any} */ (window).Shopify?.routes?.root ?? '/'}checkout`;
    leaving = true;
    try {
      if (owner && submitter instanceof HTMLButtonElement) owner.requestSubmit(submitter);
      else window.location.assign(checkoutUrl);
    } catch {
      window.location.assign(checkoutUrl);
    } finally {
      leaving = false;
    }
  };

  const gl = Boolean(pluFx?.isReady());
  active = overlay(gl);
  active.addEventListener('cancel', (cancelEvent) => {
    // Escape skips the animation, it doesn't cancel the payment
    cancelEvent.preventDefault();
    leave();
  });
  document.body.appendChild(active);
  active.showModal();

  if (gl) {
    const stage = active.querySelector('.plu-truck__stage--gl');
    try {
      truck3d = stage instanceof HTMLElement && pluFx ? pluFx.playTruck(stage) : null;
    } catch {
      truck3d = null;
    }
    // the 3D truck couldn't start: the CSS one takes its place
    if (!truck3d && stage) stage.outerHTML = cssStage();
  }

  leaveTimer = window.setTimeout(leave, LEAVE_AT);
  giveUpTimer = window.setTimeout(reset, GIVE_UP_AT);
});

// Coming back from the checkout with the browser's back button
window.addEventListener('pageshow', (event) => {
  if (event.persisted) reset();
});
